import { FC, useState } from "react";

import { serverGetSectionScores } from "api/api-server-data";
import { CourseMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { ArrowDownIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCurrentOrg, selectSections, selectTerms } from "store/selectors";
import { toastErrorMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { downloadCSVFile } from "utils/file";
import { generateSectionScoresCSV } from "utils/reporting";

export const StudentSectionScoresReport: FC = () => {
  const [selectedTermId, setSelectedTermId] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const terms = useAppSelector(selectTerms);
  const sections = useAppSelector(selectSections);
  const currentOrg = useAppSelector(selectCurrentOrg);

  const handleDownloadCSV = async () => {
    if (!selectedTermId) {
      toastErrorMessage(t("assessment_summary_report.select_term_to_view_data"));
      return;
    }

    try {
      const data = await serverGetSectionScores(selectedTermId, selectedCourseId || undefined);

      if (!data || data.length === 0) {
        toastErrorMessage(t("assessment_summary_report.no_data"));
        return;
      }

      const selectedTerm = terms.find((term) => term.id === selectedTermId);
      const termTitle = selectedTerm?.title || selectedTermId;

      // Filter sections to only those in the selected term
      const termSections = sections.filter((section) => section.term_id === selectedTermId);

      const { csvData, filename } = generateSectionScoresCSV(
        data,
        termTitle,
        termSections,
        currentOrg?.section_score_display_format,
        currentOrg?.rubric,
      );
      trackEvent("Home - Reports / Download section scores", {
        ...BUTTON_EVENT_CATEGORY,
        fileName: filename,
      });
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("assessment_summary_report.export_failed"));
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="h3">{t("student_section_scores_report.title")}</h2>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <TermMenu
            defaultToAll={false}
            direction="top"
            eventPrefix="Home - Reports / "
            menuType="menu"
            selectedTermId={selectedTermId}
            size="s"
            onTermChange={setSelectedTermId}
          />
        </div>

        <div className="flex items-center gap-2">
          <CourseMenu
            direction="top"
            menuType="menu"
            selectedCourseId={selectedCourseId}
            size="s"
            onCourseChange={setSelectedCourseId}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            disabled={!selectedTermId}
            eventName="Student Section Scores Report - Download CSV"
            kind="white"
            size="s"
            onClick={handleDownloadCSV}
          >
            <IconText
              iconEnd={<ArrowDownIcon />}
              text={t("student_section_scores_report.download_csv")}
            />
          </Button>
        </div>
      </div>
    </div>
  );
};
